/**
 * Clés TanStack Query centralisées.
 *
 * Pourquoi ? — Avoir une seule source de vérité pour les clés évite les bugs
 * d'invalidation et facilite l'autocomplétion.
 *
 * Convention : `["domain", "subdomain", ...args]`
 */
export const queryKeys = {
  // ---- Identité ------------------------------------------------------------
  auth: {
    me: () => ["auth", "me"] as const,
    roles: (userId: string) => ["auth", "roles", userId] as const,
    scopes: (userId: string) => ["auth", "scopes", userId] as const,
  },

  // ---- Profil --------------------------------------------------------------
  profile: {
    byUserId: (userId: string) => ["profile", userId] as const,
  },

  // ---- Salariés ------------------------------------------------------------
  salarie: {
    mine: (userId: string) => ["salarie", "mine", userId] as const,
    byId: (id: string) => ["salarie", id] as const,
    byOperation: (operationId: string) => ["salarie", "operation", operationId] as const,
  },

  // ---- Dossiers ------------------------------------------------------------
  dossier: {
    mine: (userId: string) => ["dossier", "mine", userId] as const,
    byId: (id: string) => ["dossier", id] as const,
    documents: (dossierId: string) => ["dossier", dossierId, "documents"] as const,
    scenarios: (dossierId: string) => ["dossier", dossierId, "scenarios"] as const,
    statusHistory: (dossierId: string) => ["dossier", dossierId, "status-history"] as const,
    hypotheses: (dossierId: string) => ["dossier", dossierId, "hypotheses"] as const,
  },

  // ---- Opérations ----------------------------------------------------------
  operation: {
    list: () => ["operations"] as const,
    byId: (id: string) => ["operation", id] as const,
    salaries: (operationId: string) => ["operation", operationId, "salaries"] as const,
  },

  // ---- Sociétés / hiérarchie ----------------------------------------------
  tenancy: {
    groupes: () => ["tenancy", "groupes"] as const,
    societes: () => ["tenancy", "societes"] as const,
    societe: (id: string) => ["tenancy", "societe", id] as const,
    filiales: (societeId: string) => ["tenancy", "filiales", societeId] as const,
    sites: (societeId: string) => ["tenancy", "sites", societeId] as const,
    businessUnits: (societeId: string) => ["tenancy", "business-units", societeId] as const,
  },

  // ---- BRI -----------------------------------------------------------------
  bri: {
    byDossier: (dossierId: string) => ["bri", "dossier", dossierId] as const,
    byId: (id: string) => ["bri", id] as const,
    matrices: () => ["bri", "matrices"] as const,
    annexesLibrary: () => ["bri", "annexes-library"] as const,
  },

  // ---- Messagerie ----------------------------------------------------------
  messaging: {
    conversations: (userId: string) => ["messaging", "conversations", userId] as const,
    messages: (conversationId: string) => ["messaging", "messages", conversationId] as const,
  },

  // ---- RDV -----------------------------------------------------------------
  appointments: {
    byDossier: (dossierId: string) => ["appointments", "dossier", dossierId] as const,
    mine: (userId: string) => ["appointments", "mine", userId] as const,
    consultantUpcoming: (consultantId: string) =>
      ["appointments", "consultant-upcoming", consultantId] as const,
    availabilities: (consultantId: string) =>
      ["appointments", "availabilities", consultantId] as const,
    openSlots: (consultantId: string) => ["appointments", "open-slots", consultantId] as const,
  },

  // ---- Formations ----------------------------------------------------------
  formations: {
    byOperation: (operationId: string) => ["formations", "operation", operationId] as const,
    inscriptionsBySalarie: (salarieId: string) =>
      ["formations", "inscriptions", salarieId] as const,
  },
} as const;
